import { Bell, Calendar, Tag, ArrowRight } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import SEOHead from '@/components/seo/SEOHead';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { Button } from '@/components/ui/button';

const Updates = () => {
  // Sample updates - in production, these would come from Google Business Profile API
  const updates = [
    {
      id: 1,
      type: 'offer',
      title: 'iPhone Screen Replacement Offer',
      description: 'Get 20% off on all iPhone screen replacements this month. Valid for iPhone 12 and above models.',
      date: 'Dec 15, 2024',
      badge: 'Limited Offer',
    },
    {
      id: 2,
      type: 'update',
      title: 'Extended Working Hours',
      description: 'We are now open till 9 PM on weekdays to serve you better. Book your appointment today!',
      date: 'Dec 10, 2024',
      badge: 'New',
    },
    {
      id: 3,
      type: 'offer',
      title: 'MacBook Battery Replacement',
      description: 'Free diagnostic service with every MacBook battery replacement. Same day service available.',
      date: 'Dec 5, 2024',
      badge: 'Special',
    },
    {
      id: 4,
      type: 'update',
      title: 'Apple Watch Ultra Repair Now Available',
      description: 'We now offer complete repair services for Apple Watch Ultra and Ultra 2 models.',
      date: 'Dec 1, 2024',
      badge: 'New Service',
    },
    {
      id: 5,
      type: 'offer',
      title: 'Student Discount',
      description: '15% discount for students on all repairs. Show your valid student ID at the time of service.',
      date: 'Nov 25, 2024',
      badge: 'Students',
    },
    {
      id: 6,
      type: 'update',
      title: 'Genuine Parts Guarantee',
      description: 'All our repairs come with 6 months warranty on parts and service. Quality you can trust.',
      date: 'Nov 20, 2024',
      badge: 'Warranty',
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Offers & Updates | iFix Apple Care Patna"
        description="Latest offers on iPhone, MacBook, iPad & Apple Watch repairs in Patna. Discounts, new services & announcements from Bihar's trusted Apple service center."
        url="https://ifixappleservicecenter.in/updates"
      />
      {/* Hero Section */}
      <section className="section-padding bg-secondary pt-28">
        <div className="container-apple">
          <PageBreadcrumb 
            items={[{ name: 'Updates & Offers', path: '/updates' }]} 
            className="mb-6"
          />
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-background rounded-full text-sm text-muted-foreground mb-6">
              <Bell className="w-4 h-4" />
              <span>Stay Updated</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mb-6 opacity-0 animate-fade-in">
              Current Updates
              <span className="block text-primary">& Offers</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed opacity-0 animate-fade-in animation-delay-100">
              Stay informed about our latest services, special offers, and important announcements from iFix Apple Care.
            </p>
          </div>
        </div>
      </section>

      {/* Updates Grid */}
      <section className="section-padding">
        <div className="container-apple">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {updates.map((update, index) => (
              <article
                key={update.id}
                className="bg-card rounded-2xl border border-border p-6 hover:shadow-apple-md transition-all opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    update.type === 'offer' 
                      ? 'bg-[hsl(142_70%_45%)]/10 text-[hsl(142_70%_35%)]'
                      : 'bg-primary/10 text-primary'
                  }`}>
                    {update.badge}
                  </span>
                  <div className="flex items-center gap-1 text-muted-foreground text-xs">
                    <Calendar className="w-3 h-3" />
                    {update.date}
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {update.title}
                </h3>
                
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  {update.description}
                </p>
                
                {update.type === 'offer' && (
                  <Button variant="apple-ghost" size="sm" className="p-0 h-auto">
                    <span>Claim Offer</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-secondary">
        <div className="container-apple text-center">
          <Tag className="w-12 h-12 text-primary mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Don't Miss Our Offers
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Follow us on social media or visit our Google Business Profile to stay updated on the latest offers and services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="apple-blue" size="lg" asChild>
              <a
                href="https://g.co/kgs/ifixapplecare"
                target="_blank"
                rel="noopener noreferrer"
              >
                View on Google
                <ArrowRight className="w-5 h-5" />
              </a>
            </Button>
            <Button variant="apple-outline" size="lg" asChild>
              <a
                href="https://instagram.com/ifixapplecare"
                target="_blank"
                rel="noopener noreferrer"
              >
                Follow on Instagram
              </a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Updates;
